import React from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Tag,
  Plus,
  Trash2,
  Pencil,
  Check,
  X,
  TrendingUp,
  Users,
  Euro,
  BadgePercent,
  Info,
  Calendar as CalendarIcon,
} from "lucide-react";

const CONTEXT_LABEL = {
  any: "Tout",
  subscription_only: "Abonnement",
  devis: "Devis IA",
};

const emptyForm = {
  code: "",
  active: true,
  type: "fixed",
  value: 20,
  min_amount: 0,
  first_use_only: true,
  context: "any",
  requires_url: false,
  label: "",
  max_uses: "",
  expires_at: "",
};

export default function MarketingTab() {
  const [codes, setCodes] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null); // "new" | promo.code | null
  const [form, setForm] = React.useState(emptyForm);
  const [saving, setSaving] = React.useState(false);
  const [showUses, setShowUses] = React.useState(null); // promo.code

  const load = React.useCallback(async () => {
    try {
      const r = await api.get("/admin/promo-codes");
      setCodes(r.data.codes || []);
    } catch {
      toast.error("Erreur chargement codes promo");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setForm(emptyForm);
    setEditing("new");
  };

  const openEdit = (c) => {
    setForm({
      code: c.code,
      active: c.active,
      type: c.type,
      value: c.value,
      min_amount: c.min_amount || 0,
      first_use_only: !!c.first_use_only,
      context: c.context || "any",
      requires_url: !!c.requires_url,
      label: c.label || "",
      max_uses: c.max_uses ?? "",
      expires_at: c.expires_at ? c.expires_at.slice(0, 10) : "",
    });
    setEditing(c.code);
  };

  const closeForm = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const save = async () => {
    if (!form.code.trim()) {
      toast.error("Code requis");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        code: form.code.trim().toUpperCase(),
        value: Number(form.value) || 0,
        min_amount: Number(form.min_amount) || 0,
        max_uses: form.max_uses === "" ? null : Number(form.max_uses),
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        label: form.label.trim(),
      };
      if (editing === "new") {
        await api.post("/admin/promo-codes", payload);
        toast.success(`Code ${payload.code} créé`);
      } else {
        // Only send editable fields (skip code)
        const { code, ...updates } = payload;
        await api.patch(`/admin/promo-codes/${encodeURIComponent(editing)}`, updates);
        toast.success(`Code ${editing} mis à jour`);
      }
      closeForm();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/admin/promo-codes/${encodeURIComponent(c.code)}`, { active: !c.active });
      toast.success(`${c.code} ${!c.active ? "activé" : "désactivé"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer DÉFINITIVEMENT le code ${c.code} ? Les utilisations passées seront conservées.`)) return;
    try {
      await api.delete(`/admin/promo-codes/${encodeURIComponent(c.code)}`);
      toast.success(`${c.code} supprimé`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  if (loading) {
    return <div className="mt-6 text-slate-500">Chargement…</div>;
  }

  // Aggregate top-line stats
  const totalUses = codes.reduce((sum, c) => sum + (c.stats?.count || 0), 0);
  const totalDiscount = codes.reduce((sum, c) => sum + (c.stats?.total_discount_eur || 0), 0);
  const totalRevenue = codes.reduce((sum, c) => sum + (c.stats?.total_revenue_eur || 0), 0);
  const activeCount = codes.filter((c) => c.active).length;

  return (
    <div className="mt-6" data-testid="marketing-tab">
      {/* Header + KPI strip */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h3 className="text-lg font-semibold text-navy flex items-center gap-2">
            <Tag className="h-5 w-5 text-[#5BA4D4]" />
            Marketing — Codes promo
          </h3>
          <p className="text-sm text-slate-500 mt-0.5">
            Crée, active et suis tes codes de réduction. La règle « 1 fois par email » se coche dans le formulaire.
          </p>
        </div>
        <button
          onClick={openNew}
          className="btn-primary text-sm"
          data-testid="promo-new-btn"
        >
          <Plus className="h-4 w-4" />
          Nouveau code
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard icon={<Tag className="h-4 w-4" />} label="Codes actifs" value={activeCount} testid="mkt-active" />
        <StatCard icon={<Users className="h-4 w-4" />} label="Utilisations" value={totalUses} testid="mkt-uses" />
        <StatCard icon={<BadgePercent className="h-4 w-4" />} label="Sconto totale" value={`€${Math.round(totalDiscount)}`} testid="mkt-discount" color="text-red-600" />
        <StatCard icon={<Euro className="h-4 w-4" />} label="CA généré" value={`€${Math.round(totalRevenue)}`} testid="mkt-revenue" color="text-emerald-600" />
      </div>

      {/* List */}
      {codes.length === 0 ? (
        <p className="text-slate-500 py-8 text-center" data-testid="no-promo">
          Aucun code promo pour le moment. Crée le premier !
        </p>
      ) : (
        <div className="grid gap-3">
          {codes.map((c) => (
            <PromoRow
              key={c.code}
              c={c}
              onEdit={() => openEdit(c)}
              onToggle={() => toggleActive(c)}
              onDelete={() => remove(c)}
              onShowUses={() => setShowUses(c.code)}
            />
          ))}
        </div>
      )}

      {/* Form modal */}
      {editing ? (
        <PromoFormModal
          form={form}
          setForm={setForm}
          isNew={editing === "new"}
          onClose={closeForm}
          onSave={save}
          saving={saving}
        />
      ) : null}

      {/* Uses drill-down */}
      {showUses ? (
        <PromoUsesModal code={showUses} onClose={() => setShowUses(null)} />
      ) : null}
    </div>
  );
}

function StatCard({ icon, label, value, testid, color = "text-[#5BA4D4]" }) {
  return (
    <div className="card-clean p-4" data-testid={testid}>
      <div className={`inline-flex h-7 w-7 items-center justify-center rounded-md bg-slate-50 ${color}`}>
        {icon}
      </div>
      <p className="mt-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</p>
      <p className="text-xl font-bold text-navy mt-0.5">{value}</p>
    </div>
  );
}

function PromoRow({ c, onEdit, onToggle, onDelete, onShowUses }) {
  const isExpired = c.expires_at && c.expires_at < new Date().toISOString();
  const isExhausted = c.max_uses && (c.uses_count || 0) >= c.max_uses;
  return (
    <div className="card-clean p-5" data-testid={`promo-row-${c.code}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="rounded-md bg-navy text-white px-2 py-1 text-xs font-bold font-mono tracking-wide">
              {c.code}
            </span>
            {c.active && !isExpired && !isExhausted ? (
              <span className="rounded-full bg-emerald-100 text-emerald-700 text-[10px] px-2 py-0.5 font-semibold uppercase tracking-wide">
                Actif
              </span>
            ) : (
              <span className="rounded-full bg-slate-200 text-slate-600 text-[10px] px-2 py-0.5 font-semibold uppercase tracking-wide">
                {isExpired ? "Expiré" : isExhausted ? "Épuisé" : "Désactivé"}
              </span>
            )}
            {c.first_use_only ? (
              <span className="rounded-full bg-amber-100 text-amber-700 text-[10px] px-2 py-0.5 font-semibold uppercase tracking-wide">
                1 fois / client
              </span>
            ) : null}
            {c.context !== "any" ? (
              <span className="rounded-full bg-purple-100 text-purple-700 text-[10px] px-2 py-0.5 font-semibold uppercase tracking-wide">
                {CONTEXT_LABEL[c.context] || c.context}
              </span>
            ) : null}
          </div>
          {c.label ? <p className="mt-1.5 text-sm text-slate-700 italic">{c.label}</p> : null}
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
            <span className="flex items-center gap-1 font-semibold text-navy">
              {c.type === "percent" ? <><BadgePercent className="h-3 w-3" /> -{c.value}%</> : <><Euro className="h-3 w-3" /> -€{c.value}</>}
            </span>
            {c.min_amount > 0 ? (
              <span>Min. commande : €{c.min_amount}</span>
            ) : null}
            {c.max_uses ? (
              <span>Utilisations : {c.uses_count || 0}/{c.max_uses}</span>
            ) : (
              <span>Utilisations : {c.uses_count || 0}</span>
            )}
            {c.expires_at ? (
              <span className="flex items-center gap-1">
                <CalendarIcon className="h-3 w-3" />
                Expire : {c.expires_at.slice(0, 10)}
              </span>
            ) : null}
          </div>
          {c.stats?.count > 0 ? (
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
              <span className="text-red-700"><b>-€{Math.round(c.stats.total_discount_eur)}</b> réduction totale</span>
              <span className="text-emerald-700"><b>+€{Math.round(c.stats.total_revenue_eur)}</b> CA généré</span>
              {c.stats.last_used_at ? (
                <span className="text-slate-500">Dernière utilisation : {c.stats.last_used_at.slice(0, 10)}</span>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 items-end">
          {(c.stats?.count || 0) > 0 ? (
            <button
              onClick={onShowUses}
              className="text-xs rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 font-medium flex items-center gap-1"
              data-testid={`promo-uses-${c.code}`}
              title="Voir les utilisations"
            >
              <TrendingUp className="h-3 w-3" /> Utilisations
            </button>
          ) : null}
          <button
            onClick={onToggle}
            className={`text-xs rounded-lg px-3 py-1.5 font-medium flex items-center gap-1 ${
              c.active
                ? "bg-amber-100 hover:bg-amber-200 text-amber-800"
                : "bg-emerald-100 hover:bg-emerald-200 text-emerald-800"
            }`}
            data-testid={`promo-toggle-${c.code}`}
          >
            {c.active ? <><X className="h-3 w-3" /> Désactiver</> : <><Check className="h-3 w-3" /> Activer</>}
          </button>
          <button
            onClick={onEdit}
            className="text-xs rounded-lg bg-sky-100 hover:bg-sky-200 text-sky-700 px-3 py-1.5 font-medium flex items-center gap-1"
            data-testid={`promo-edit-${c.code}`}
          >
            <Pencil className="h-3 w-3" /> Éditer
          </button>
          <button
            onClick={onDelete}
            className="text-xs rounded-lg bg-red-50 hover:bg-red-100 text-red-600 px-3 py-1.5 font-medium flex items-center gap-1"
            data-testid={`promo-delete-${c.code}`}
          >
            <Trash2 className="h-3 w-3" /> Supprimer
          </button>
        </div>
      </div>
    </div>
  );
}

function PromoFormModal({ form, setForm, isNew, onClose, onSave, saving }) {
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="promo-form-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col"
      >
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h4 className="font-semibold text-navy flex items-center gap-2">
            <Tag className="h-4 w-4" />
            {isNew ? "Nouveau code promo" : `Éditer ${form.code}`}
          </h4>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="promo-form-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          <Field label="Code" required>
            <input
              type="text"
              value={form.code}
              disabled={!isNew}
              onChange={(e) => set("code")(e.target.value.toUpperCase())}
              placeholder="ex: VOISIN20"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm uppercase font-mono disabled:bg-slate-100 disabled:text-slate-500"
              data-testid="promo-form-code"
            />
            {!isNew && (
              <p className="mt-1 text-[11px] text-slate-500 italic">Le code ne peut pas être modifié après création (les utilisations passées y sont liées).</p>
            )}
          </Field>

          <Field label="Libellé (affiché au client)">
            <input
              type="text"
              value={form.label}
              onChange={(e) => set("label")(e.target.value)}
              placeholder="ex: Offre de bienvenue -20€"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              data-testid="promo-form-label"
              maxLength={200}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <select
                value={form.type}
                onChange={(e) => set("type")(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                data-testid="promo-form-type"
              >
                <option value="fixed">Montant fixe (€)</option>
                <option value="percent">Pourcentage (%)</option>
              </select>
            </Field>
            <Field label={form.type === "percent" ? "Valeur (%)" : "Valeur (€)"} required>
              <input
                type="number"
                value={form.value}
                onChange={(e) => set("value")(e.target.value)}
                min={0}
                step={form.type === "percent" ? 1 : 5}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                data-testid="promo-form-value"
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Commande min. (€)">
              <input
                type="number"
                value={form.min_amount}
                onChange={(e) => set("min_amount")(e.target.value)}
                min={0}
                step={10}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                data-testid="promo-form-min"
              />
            </Field>
            <Field label="Max utilisations totales">
              <input
                type="number"
                value={form.max_uses}
                onChange={(e) => set("max_uses")(e.target.value)}
                min={1}
                step={1}
                placeholder="illimité"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                data-testid="promo-form-maxuses"
              />
            </Field>
          </div>

          <Field label="Expire le (optionnel)">
            <input
              type="date"
              value={form.expires_at}
              onChange={(e) => set("expires_at")(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              data-testid="promo-form-expires"
            />
          </Field>

          <Field label="S'applique à">
            <select
              value={form.context}
              onChange={(e) => set("context")(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              data-testid="promo-form-context"
            >
              <option value="any">Toute commande</option>
              <option value="subscription_only">Abonnement uniquement</option>
              <option value="devis">Devis IA uniquement</option>
            </select>
          </Field>

          <div className="rounded-lg bg-slate-50 p-3 space-y-2">
            <Toggle
              checked={form.active}
              onChange={set("active")}
              label="Actif"
              testid="promo-form-active"
            />
            <Toggle
              checked={form.first_use_only}
              onChange={set("first_use_only")}
              label="Une seule fois par email (ex: nouveau client)"
              testid="promo-form-firstuse"
            />
            <Toggle
              checked={form.requires_url}
              onChange={set("requires_url")}
              label="Requiert une URL Airbnb / annonce"
              testid="promo-form-requiresurl"
            />
          </div>

          <div className="rounded-lg bg-sky-50 border border-sky-200 p-3 text-xs text-sky-800 flex items-start gap-2">
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              Astuce : pour <b>« -20€ nouveau client, une seule fois »</b>, laisse la case « Une seule fois par email » cochée. Le système bloque automatiquement toute deuxième utilisation avec la même adresse.
            </span>
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="btn-outline text-sm" data-testid="promo-form-cancel">
            Annuler
          </button>
          <button onClick={onSave} disabled={saving} className="btn-primary text-sm disabled:opacity-40" data-testid="promo-form-save">
            {saving ? "…" : isNew ? "Créer le code" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block text-sm">
      <span className="font-medium text-navy">
        {label}
        {required ? <span className="text-red-500 ml-0.5">*</span> : null}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Toggle({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-slate-300 text-[#5BA4D4] focus:ring-[#5BA4D4]"
        data-testid={testid}
      />
      <span className="text-slate-700">{label}</span>
    </label>
  );
}

function PromoUsesModal({ code, onClose }) {
  const [uses, setUses] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    api
      .get(`/admin/promo-codes/${encodeURIComponent(code)}/uses`)
      .then((r) => setUses(r.data.uses || []))
      .catch(() => setUses([]))
      .finally(() => setLoading(false));
  }, [code]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="promo-uses-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
      >
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500">Utilisations du code</p>
            <p className="font-semibold text-navy font-mono">{code}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="promo-uses-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">
          {loading ? (
            <p className="text-slate-400 text-center py-6">Chargement…</p>
          ) : uses.length === 0 ? (
            <p className="text-slate-500 text-center py-6">Aucune utilisation enregistrée.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="pb-2 font-medium">Email</th>
                  <th className="pb-2 font-medium text-right">Commande</th>
                  <th className="pb-2 font-medium text-right">Sconto</th>
                  <th className="pb-2 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {uses.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100">
                    <td className="py-2 text-navy truncate max-w-[220px]">{u.email}</td>
                    <td className="py-2 text-right">€{u.amount_eur}</td>
                    <td className="py-2 text-right text-red-600">-€{u.discount_eur}</td>
                    <td className="py-2 text-slate-500 text-xs">{(u.used_at || "").slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
